import { ComponentType, MediumDomain, PortDefinition } from '../types';

/**
 * Typed ports for canvas components. Mirrors backend/elements/services/ports.py
 * — keep the two in step.
 *
 * Components that do not declare `ports` get the historical layout built from
 * `input_ports`/`output_ports`: handles `input-0..N` and `output-0..M`, all
 * liquid, all on stream `main`, so every existing schema renders and connects
 * exactly as before.
 */

export const DEFAULT_STREAM = 'main';
export const DEFAULT_DOMAIN: MediumDomain = 'liquid';

export interface ResolvedPort {
  id: string;
  direction: 'in' | 'out';
  domain: MediumDomain;
  stream: string;
  label?: string;
}

export interface PortLayout extends ResolvedPort {
  /** React Flow handle type: inlets are targets, outlets are sources. */
  handleType: 'target' | 'source';
  side: 'left' | 'right';
  /** Vertical position along the node's side, in percent of its height. */
  yPercent: number;
}

/** Handle colours per medium; liquid keeps the canvas's original grey. */
export const DOMAIN_COLORS: Record<MediumDomain, string> = {
  liquid: '#5f6368',
  gas: '#00897b',
};

type PortSource = Pick<ComponentType, 'input_ports' | 'output_ports' | 'component_type'> & {
  ports?: PortDefinition[];
};

export function defaultPorts(inputPorts: number, outputPorts: number): ResolvedPort[] {
  const ports: ResolvedPort[] = [];
  for (let i = 0; i < Math.max(0, inputPorts || 0); i++) {
    ports.push({ id: `input-${i}`, direction: 'in', domain: DEFAULT_DOMAIN, stream: DEFAULT_STREAM });
  }
  for (let i = 0; i < Math.max(0, outputPorts || 0); i++) {
    ports.push({ id: `output-${i}`, direction: 'out', domain: DEFAULT_DOMAIN, stream: DEFAULT_STREAM });
  }
  return ports;
}

function isValidDeclaration(ports: PortDefinition[]): boolean {
  const ids = new Set<string>();
  const streamDomain = new Map<string, MediumDomain>();
  for (const p of ports) {
    if (!p || typeof p.id !== 'string' || !p.id || ids.has(p.id)) return false;
    if (p.direction !== 'in' && p.direction !== 'out') return false;
    const domain = p.domain || DEFAULT_DOMAIN;
    if (domain !== 'liquid' && domain !== 'gas') return false;
    const stream = p.stream || DEFAULT_STREAM;
    const seen = streamDomain.get(stream);
    if (seen && seen !== domain) return false;
    streamDomain.set(stream, domain);
    ids.add(p.id);
  }
  return true;
}

/** Ports of a component; a malformed declaration falls back to the legacy layout. */
export function getPorts(component: PortSource | undefined | null): ResolvedPort[] {
  if (!component) return [];
  const explicit = component.ports;
  if (Array.isArray(explicit) && explicit.length > 0 && isValidDeclaration(explicit)) {
    return explicit.map(p => ({
      id: p.id,
      direction: p.direction,
      domain: p.domain || DEFAULT_DOMAIN,
      stream: p.stream || DEFAULT_STREAM,
      ...(p.label ? { label: p.label } : {}),
    }));
  }
  return defaultPorts(component.input_ports ?? 1, component.output_ports ?? 1);
}

/**
 * Where each handle sits on the node: inlets down the left side, outlets down
 * the right, evenly spread. Branch/merge nodes use a tighter 10% margin so
 * their many ports reach towards the corners; everything else uses 25%.
 */
export function getPortLayout(component: PortSource | undefined | null): PortLayout[] {
  const ports = getPorts(component);
  const isBranchOrMerge = component?.component_type === 'branch' || component?.component_type === 'merge';
  const margin = isBranchOrMerge ? 10 : 25;
  const range = 100 - 2 * margin;

  const place = (side: ResolvedPort[], sideName: 'left' | 'right'): PortLayout[] =>
    side.map((port, i) => ({
      ...port,
      handleType: port.direction === 'in' ? 'target' : 'source',
      side: sideName,
      yPercent: side.length === 1 ? 50 : margin + (i * range) / (side.length - 1),
    }));

  return [
    ...place(ports.filter(p => p.direction === 'in'), 'left'),
    ...place(ports.filter(p => p.direction === 'out'), 'right'),
  ];
}

/**
 * Resolve a handle id to a port. A missing handle (edges saved before handles
 * were recorded) means the first port in that direction.
 */
export function findPort(
  component: PortSource | undefined | null,
  handleId: string | null | undefined,
  direction: 'in' | 'out',
): ResolvedPort | undefined {
  const ports = getPorts(component);
  if (!handleId) return ports.find(p => p.direction === direction);
  return ports.find(p => p.id === handleId);
}

/** Whether an edge from `sourceHandle` to `targetHandle` joins compatible ports. */
export function canConnect(
  sourceComponent: PortSource | undefined | null,
  sourceHandle: string | null | undefined,
  targetComponent: PortSource | undefined | null,
  targetHandle: string | null | undefined,
): boolean {
  const src = findPort(sourceComponent, sourceHandle, 'out');
  const tgt = findPort(targetComponent, targetHandle, 'in');
  if (!src || !tgt) return false;
  if (src.direction !== 'out' || tgt.direction !== 'in') return false;
  return src.domain === tgt.domain;
}

export interface CanvasCircuit {
  /** 'circuit-N', numbered in schema order like the backend. */
  id: string;
  index: number;
  domain: MediumDomain;
  nodeIds: string[];
  /** First pump of the circuit, else its first node (backend: CircuitGraph.circuit_anchor). */
  anchorId: string;
}

interface CircuitNodeLike {
  id: string;
  type?: string;
  data?: { component?: PortSource & { component_type?: string } };
}

interface CircuitEdgeLike {
  source: string;
  target: string;
  sourceHandle?: string | null;
  targetHandle?: string | null;
}

/**
 * Split a canvas into independent circuits — connected (node, stream) pairs
 * joined by valid edges. Mirrors CircuitGraph.find_circuits() in
 * backend/elements/services/circuit_graph.py.
 */
export function findCircuits(nodes: CircuitNodeLike[], edges: CircuitEdgeLike[]): CanvasCircuit[] {
  const components = new Map<string, any>();
  for (const n of nodes) {
    if (n.type !== 'annotationNode' && n.data?.component) components.set(n.id, n.data.component);
  }
  const parent = new Map<string, string>();
  const find = (k: string): string => {
    if (!parent.has(k)) parent.set(k, k);
    let root = k;
    while (parent.get(root) !== root) root = parent.get(root)!;
    parent.set(k, root);
    return root;
  };
  const domainOf = new Map<string, MediumDomain>();

  for (const e of edges) {
    const src = components.get(e.source);
    const tgt = components.get(e.target);
    if (!src || !tgt) continue;
    const sp = findPort(src, e.sourceHandle, 'out');
    const tp = findPort(tgt, e.targetHandle, 'in');
    if (!sp || !tp || sp.domain !== tp.domain) continue;
    const a = `${e.source}\u0000${sp.stream}`;
    const b = `${e.target}\u0000${tp.stream}`;
    domainOf.set(a, sp.domain);
    domainOf.set(b, tp.domain);
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent.set(rb, ra);
  }

  const groups = new Map<string, CanvasCircuit>();
  const ordered: CanvasCircuit[] = [];
  components.forEach((component, nodeId) => {
    const streams: string[] = [];
    for (const p of getPorts(component)) if (!streams.includes(p.stream)) streams.push(p.stream);
    for (const stream of streams) {
      const key = `${nodeId}\u0000${stream}`;
      if (!parent.has(key)) continue;
      const root = find(key);
      let circuit = groups.get(root);
      if (!circuit) {
        circuit = {
          id: `circuit-${ordered.length + 1}`,
          index: ordered.length + 1,
          domain: domainOf.get(key) || 'liquid',
          nodeIds: [],
          anchorId: nodeId,
        };
        groups.set(root, circuit);
        ordered.push(circuit);
      }
      if (!circuit.nodeIds.includes(nodeId)) circuit.nodeIds.push(nodeId);
    }
  });

  for (const c of ordered) {
    const pump = c.nodeIds.find(id => components.get(id)?.component_type === 'pump');
    c.anchorId = pump ?? c.nodeIds[0];
  }
  return ordered;
}
